import { useLingui } from '@lingui/react/macro';
import { useMemo } from 'react';
import type { SWRResponse } from 'swr';
import { useHasAccessToken } from '@/auth/api';
import { useBackend, useBackendMutation } from '@/backend';
import { useCoins } from '@/shared/api';

export const useBalances = ({ size }: { size?: number }) => {
  const hasAccessToken = useHasAccessToken();
  return useBackend(
    'wealth:get:balances/',
    {
      top_n: size,
    },
    {
      enabled: hasAccessToken,
      swrOptions: {
        dedupingInterval: 1000 * 60,
      },
    },
  );
};

export const useBalance = (slug?: string) => {
  const hasAccessToken = useHasAccessToken();
  return useBackend(
    'wealth:get:balances/{slug}/',
    {
      slug: slug!,
    },
    {
      enabled: !!slug && hasAccessToken,
      swrOptions: {
        dedupingInterval: 1000 * 60,
      },
    },
  );
};

export type BalanceChart = {
  key: string;
  percentage: number;
  coin: string | undefined;
  name: string;
  color: string;
};

export const useBalancesChart = (): Omit<
  SWRResponse<BalanceChart[]>,
  'mutate'
> => {
  const { t, i18n } = useLingui();
  const balances = useBalances({
    size: 4,
  });
  const coins = useCoins();

  const data = useMemo(() => {
    const ret =
      balances.data?.balances
        .map((balance) => {
          const coin = coins.data?.find((x) => x.slug === balance.coin);
          if (!coin) return null;
          return {
            key: balance.coin,
            coin: balance.coin,
            name: i18n.locale !== 'en' ? coin.iraqi_name : coin?.symbol,
            percentage: balance.percentage_of_total,
            color:
              coins.data?.find((x) => x.slug === balance.coin)?.color ??
              'black',
          } as BalanceChart;
        })
        .filter((x) => !!x) ?? [];
    if (balances.data?.other && +balances.data.other.balance_iqd > 0) {
      ret?.push({
        key: 'other__',
        coin: undefined,
        name: t`Other`,
        percentage: balances.data.other.percentage_of_total,
        color: 'gray',
      });
    }
    return ret;
  }, [balances.data, coins.data, i18n.locale, t]);

  return {
    data: coins.isLoading || balances.isLoading ? undefined : data,
    error: coins.error || balances.error,
    isLoading: coins.isLoading || balances.isLoading,
    isValidating: coins.isValidating || balances.isValidating,
  };
};

export const useRefreshWallet = () =>
  useBackendMutation('network-manager:post:refresh/', {
    revalidate: ['wealth:*', 'network-manager:*'],
  });
